
import React, { useState, useEffect, useRef } from 'react';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/useAuth';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import SearchableCustomerSelect from '@/components/shared/SearchableCustomerSelect';
import { CalendarIcon, Package, Plus, ArrowLeft, Receipt, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';
import { Link } from 'react-router-dom';
import AddInvoiceLineItemDialog, { type InvoiceLineItemData } from './AddInvoiceLineItemDialog';
import InvoiceCatalogPicker, { type CatalogLineItemData } from './InvoiceCatalogPicker';
import EntityActions from '@/components/shared/EntityActions';
import EditLineItemDialog from './EditLineItemDialog';
import FormActions from '@/components/shared/FormActions';

const lineItemSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  base_unit: z.string().min(1, 'Unit is required'),
  quantity: z.number().min(0.01, 'Quantity must be greater than 0'),
  base_cost: z.number().min(0, 'Base cost must be 0 or greater'),
  markup_percentage: z.number().min(0, 'Markup must be 0 or greater'),
  unit_cost: z.number().min(0, 'Unit cost must be 0 or greater'),
  discount_percentage: z.number().min(0).max(100).default(0),
  category_id: z.string().optional(),
  source_item_id: z.string().optional(),
  source_type: z.enum(['internal', 'supplier', 'custom']).default('custom'),
});

const standaloneInvoiceSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  customer_id: z.string().min(1, 'Customer is required'),
  due_date: z.date().optional(),
  payment_terms: z.string().optional(),
  special_instructions: z.string().optional(),
  line_items: z.array(lineItemSchema).min(1, 'At least one line item is required'),
});

type LineItem = z.infer<typeof lineItemSchema>;
type StandaloneInvoiceFormValues = z.infer<typeof standaloneInvoiceSchema>;

interface StandaloneInvoiceFormProps {
  preselectedCustomerId?: string;
  isEditMode?: boolean;
  invoiceId?: string;
  initialData?: any;
}

const StandaloneInvoiceForm: React.FC<StandaloneInvoiceFormProps> = ({ 
  preselectedCustomerId, 
  isEditMode = false,
  invoiceId,
  initialData,
}) => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [editingLineItemIndex, setEditingLineItemIndex] = useState<number | null>(null);

  const { data: customers } = useQuery({
    queryKey: ['customersForInvoice'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, last_name, business_name, customer_type, representative_first_name, representative_last_name');
      if (error) throw error;
      return (data || []).sort((a, b) =>
        getCustomerDisplayName(a).localeCompare(getCustomerDisplayName(b))
      );
    },
  });

  const { data: company } = useQuery({
    queryKey: ['companyForInvoice', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return null;
      const { data, error } = await supabase
        .from('companies')
        .select('id, is_vat_registered, "VAT Rate"')
        .eq('id', profile.company_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.company_id,
  });

  // Fetch existing line items in edit mode
  const { data: existingLineItems } = useQuery({
    queryKey: ['invoiceLineItems', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_line_items')
        .select('*')
        .eq('invoice_id', invoiceId!)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: isEditMode && !!invoiceId,
  });

  const form = useForm<StandaloneInvoiceFormValues>({
    resolver: zodResolver(standaloneInvoiceSchema),
    defaultValues: {
      title: '',
      description: '',
      customer_id: preselectedCustomerId || (isEditMode && initialData?.customer_id ? initialData.customer_id : ''),
      payment_terms: '',
      special_instructions: '',
      line_items: [],
    },
  });

  // Initialize form with existing data in edit mode
  useEffect(() => {
    if (isEditMode && initialData && existingLineItems && !isInitialized) {
      const mappedLineItems: LineItem[] = existingLineItems.map(item => ({
        description: item.description,
        base_unit: item.base_unit,
        quantity: item.quantity_invoiced,
        base_cost: item.base_cost || 0,
        markup_percentage: item.markup_percentage || 0,
        unit_cost: item.unit_cost,
        discount_percentage: (item as any).discount_percentage || 0,
        category_id: item.category_id || undefined,
        source_item_id: item.source_item_id || undefined,
        source_type: (item.source_type as 'internal' | 'supplier' | 'custom') || 'custom',
      }));

      form.reset({
        title: initialData.title || '',
        description: initialData.description || '',
        customer_id: initialData.customer_id || '',
        due_date: initialData.due_date ? new Date(initialData.due_date) : undefined,
        payment_terms: initialData.payment_terms || '',
        special_instructions: initialData.special_instructions || '',
        line_items: mappedLineItems,
      });
      setIsInitialized(true);
    }
  }, [isEditMode, initialData, existingLineItems, isInitialized, form]);

  const lineItems = form.watch('line_items') || [];

  // Helper to update a single field on a line item and recalculate derived values
  const updateLineItemField = (index: number, field: keyof LineItem, value: number) => {
    const items = [...form.getValues('line_items')];
    const item = { ...items[index], [field]: value };
    // Recalculate unit_cost when markup changes
    if (field === 'markup_percentage') {
      item.unit_cost = item.base_cost * (1 + value / 100);
    }
    items[index] = item;
    form.setValue('line_items', items, { shouldValidate: true });
  };

  const subtotal = lineItems.reduce((sum, item) => {
    return sum + (item.quantity || 0) * (item.unit_cost || 0);
  }, 0);

  const isVatRegistered = company?.is_vat_registered || false;
  const vatRate = company?.['VAT Rate'] || 0;
  const taxAmount = isVatRegistered && vatRate > 0 ? (subtotal * vatRate) / 100 : 0;
  const totalAmount = subtotal + taxAmount;

  const addLineItem = (item: LineItem) => {
    const current = form.getValues('line_items') || [];
    form.setValue('line_items', [...current, item], { shouldValidate: true });
  };

  const removeLineItem = (index: number) => {
    const current = form.getValues('line_items') || [];
    form.setValue('line_items', current.filter((_, i) => i !== index), { shouldValidate: true });
  };

  const handleCustomItemSubmit = (data: InvoiceLineItemData) => {
    addLineItem(data);
    setShowCustomDialog(false);
  };

  const handleCatalogItemSubmit = (data: CatalogLineItemData) => {
    addLineItem(data);
    setShowCatalogPicker(false);
  };

  const updateMutation = useMutation({
    mutationFn: async (values: StandaloneInvoiceFormValues) => {
      if (!invoiceId) throw new Error('Missing invoice ID');

      // Update invoice fields
      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({
          title: values.title,
          description: values.description,
          customer_id: values.customer_id,
          due_date: values.due_date
            ? `${values.due_date.getFullYear()}-${String(values.due_date.getMonth() + 1).padStart(2, '0')}-${String(values.due_date.getDate()).padStart(2, '0')}`
            : null,
          payment_terms: values.payment_terms,
          special_instructions: values.special_instructions,
          tax_percentage: isVatRegistered ? vatRate : 0,
          tax_inclusive: false,
          subtotal: subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          updated_at: new Date().toISOString(),
        })
        .eq('id', invoiceId);

      if (invoiceError) throw invoiceError;

      // Delete old line items and insert new ones
      const { error: deleteError } = await supabase
        .from('invoice_line_items')
        .delete()
        .eq('invoice_id', invoiceId);

      if (deleteError) throw deleteError;

      const invoiceLineItems = values.line_items.map((item, index) => {
        const grossTotal = item.quantity * item.unit_cost;
        const discount = item.discount_percentage || 0;
        const totalCost = grossTotal * (1 - discount / 100);
        return {
          invoice_id: invoiceId,
          estimate_line_item_id: null,
          description: item.description,
          base_unit: item.base_unit,
          quantity_invoiced: item.quantity,
          unit_cost: item.unit_cost,
          total_cost: totalCost,
          sort_order: index,
          is_partial_invoice: false,
          base_cost: item.base_cost,
          markup_percentage: item.markup_percentage,
          discount_percentage: discount,
          category_id: item.category_id || null,
          source_item_id: item.source_item_id || null,
          source_type: item.source_type,
        };
      });

      const { error: lineItemsError } = await supabase
        .from('invoice_line_items')
        .insert(invoiceLineItems);

      if (lineItemsError) throw lineItemsError;

      return { id: invoiceId };
    },
    onSuccess: () => {
      toast.success('Invoice updated successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoiceLineItems', invoiceId] });
      navigate(`/invoices/${invoiceId}`);
    },
    onError: (error) => {
      console.error('Error updating standalone invoice:', error);
      toast.error('Failed to update invoice');
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: StandaloneInvoiceFormValues) => {
      if (!user?.id || !profile?.company_id) throw new Error('Not authenticated');

      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          company_id: profile.company_id,
          estimate_id: null,
          title: values.title,
          description: values.description,
          customer_id: values.customer_id,
          created_by: user.id,
          due_date: values.due_date
            ? `${values.due_date.getFullYear()}-${String(values.due_date.getMonth() + 1).padStart(2, '0')}-${String(values.due_date.getDate()).padStart(2, '0')}`
            : null,
          payment_terms: values.payment_terms,
          special_instructions: values.special_instructions,
          tax_percentage: isVatRegistered ? vatRate : 0,
          tax_inclusive: false,
          subtotal: subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
           invoice_type: 'standalone',
          invoice_date: new Date().toISOString(),
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      const invoiceLineItems = values.line_items.map((item, index) => {
        const grossTotal = item.quantity * item.unit_cost;
        const discount = item.discount_percentage || 0;
        const totalCost = grossTotal * (1 - discount / 100);
        return {
          invoice_id: invoice.id,
          estimate_line_item_id: null,
          description: item.description,
          base_unit: item.base_unit,
          quantity_invoiced: item.quantity,
          unit_cost: item.unit_cost,
          total_cost: totalCost,
          sort_order: index,
          is_partial_invoice: false,
          base_cost: item.base_cost,
          markup_percentage: item.markup_percentage,
          discount_percentage: discount,
          category_id: item.category_id || null,
          source_item_id: item.source_item_id || null,
          source_type: item.source_type,
        };
      });

      const { error: lineItemsError } = await supabase
        .from('invoice_line_items')
        .insert(invoiceLineItems);

      if (lineItemsError) throw lineItemsError;

      return invoice;
    },
    onSuccess: (invoice) => {
      toast.success('Invoice created successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate(`/invoices/${invoice.id}`);
    },
    onError: (error) => {
      console.error('Error creating standalone invoice:', error);
      toast.error('Failed to create invoice');
    },
  });

  const activeMutation = isEditMode ? updateMutation : createMutation;

  const onSubmit = (values: StandaloneInvoiceFormValues) => {
    activeMutation.mutate(values);
  };

  const getCustomerLabel = (c: any) => {
    return getCustomerDisplayName(c);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{isEditMode ? 'Edit Standalone Invoice' : 'Create Standalone Invoice'}</h1>
        <p className="text-sm text-muted-foreground">{isEditMode ? 'Edit invoice details and line items' : 'Create an invoice without linking to an estimate'}</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Receipt className="h-5 w-5" />
                Invoice Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice Title</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter invoice title" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="customer_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer</FormLabel>
                      <FormControl>
                        <SearchableCustomerSelect
                          value={field.value}
                          onValueChange={field.onChange}
                          customers={customers}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button variant="outline" className="w-full pl-3 text-left font-normal">
                              {field.value ? format(field.value, 'dd MMMM yyyy') : <span>Pick a due date</span>}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={field.value} onSelect={field.onChange} initialFocus className="pointer-events-auto" />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Optional description" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="payment_terms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Terms</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Enter payment terms" rows={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="special_instructions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Special Instructions</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Enter special instructions" rows={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-lg">Line Items</CardTitle>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-initial"
                    onClick={() => setShowCatalogPicker(true)}
                  >
                    <Package className="h-4 w-4 mr-2" />
                    From Catalog
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-initial"
                    onClick={() => setShowCustomDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Custom Item
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {lineItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No line items yet</p>
                  <p className="text-sm">Add items from the catalog or create custom items</p>
                </div>
              ) : (
                <div className="space-y-2 overflow-hidden min-w-0">
                  {/* Header row - hidden on mobile */}
                  <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_70px_50px_90px_70px_90px_90px_36px] gap-2 text-sm font-medium text-muted-foreground px-1">
                    <span>Description</span>
                    <span>Qty</span>
                    <span>Unit</span>
                    <span>Base Cost</span>
                    <span>Markup%</span>
                    <span>Unit Price</span>
                    <span>Total</span>
                    <span></span>
                  </div>

                  {lineItems.map((item, index) => {
                    const lineTotal = (item.quantity || 0) * (item.unit_cost || 0);
                    const lineActions = [
                      {
                        label: 'Edit',
                        icon: Pencil,
                        onClick: () => setEditingLineItemIndex(index),
                      },
                      {
                        label: 'Delete',
                        icon: Trash2,
                        onClick: () => removeLineItem(index),
                        destructive: true,
                      },
                    ];
                    return (
                      <div key={index} className="min-w-0">
                        {/* Mobile card layout */}
                        <div className="md:hidden p-3 border rounded-lg bg-muted/30 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-medium break-words line-clamp-2">{item.description}</span>
                              {item.source_type !== 'custom' && (
                                <span className={`text-xs px-1.5 py-0.5 rounded border flex-shrink-0 ${
                                  item.source_type === 'internal'
                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                    : 'bg-green-50 text-green-700 border-green-200'
                                }`}>
                                  {item.source_type}
                                </span>
                              )}
                            </div>
                            <EntityActions actions={lineActions} size="sm" />
                          </div>
                          <div className="space-y-1.5 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Base Cost</span>
                              <span className="font-mono whitespace-nowrap">{formatCurrency(item.base_cost)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Markup</span>
                              <span className="font-mono whitespace-nowrap">{item.markup_percentage}%</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Unit Price</span>
                              <span className="font-mono whitespace-nowrap">{formatCurrency(item.unit_cost)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">Qty</span>
                              <div className="flex items-center gap-1.5">
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '') updateLineItemField(index, 'quantity', '' as any);
                                    else { const num = parseFloat(val); if (!isNaN(num)) updateLineItemField(index, 'quantity', num); }
                                  }}
                                  onBlur={() => { if ((item.quantity as any) === '' || item.quantity === undefined) updateLineItemField(index, 'quantity', 0); }}
                                  className="h-7 w-20 px-1.5 text-xs font-mono text-right border-transparent bg-transparent hover:border-input focus:border-input focus:bg-background transition-colors"
                                  step="0.01"
                                  min="0.01"
                                />
                                <span className="text-xs text-muted-foreground w-8">{item.base_unit}</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between border-t pt-1.5">
                              <span className="text-muted-foreground font-medium">Total</span>
                              <span className="font-mono font-medium whitespace-nowrap">{formatCurrency(lineTotal)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Desktop grid layout */}
                        <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_70px_50px_90px_70px_90px_90px_36px] gap-2 items-center p-1">
                          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                            <span className="text-sm font-medium truncate">{item.description}</span>
                            {item.source_type !== 'custom' && (
                              <span className={`text-xs px-1.5 py-0.5 rounded border ${
                                item.source_type === 'internal'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-green-50 text-green-700 border-green-200'
                              }`}>
                                {item.source_type}
                              </span>
                            )}
                          </div>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') updateLineItemField(index, 'quantity', '' as any);
                              else { const num = parseFloat(val); if (!isNaN(num)) updateLineItemField(index, 'quantity', num); }
                            }}
                            onBlur={() => { if ((item.quantity as any) === '' || item.quantity === undefined) updateLineItemField(index, 'quantity', 0); }}
                            className="h-7 px-1.5 text-sm font-mono border-transparent bg-transparent hover:border-input focus:border-input focus:bg-background transition-colors"
                            step="0.01"
                            min="0.01"
                          />
                          <span className="text-sm text-muted-foreground">{item.base_unit}</span>
                          <span className="text-sm font-mono">{formatCurrency(item.base_cost)}</span>
                          <span className="text-sm font-mono">{item.markup_percentage}%</span>
                          <span className="text-sm font-mono">{formatCurrency(item.unit_cost)}</span>
                          <span className="text-sm font-mono font-medium">{formatCurrency(lineTotal)}</span>
                          <EntityActions actions={lineActions} size="sm" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {form.formState.errors.line_items?.message && (
                <p className="text-sm text-destructive mt-2">{form.formState.errors.line_items.message}</p>
              )}
            </CardContent>
          </Card>

          {/* Totals */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-end gap-2">
                <div className="flex justify-between w-full max-w-xs text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-medium">{formatCurrency(subtotal)}</span>
                </div>
                {isVatRegistered && vatRate > 0 && (
                  <div className="flex justify-between w-full max-w-xs text-sm">
                    <span className="text-muted-foreground">VAT ({vatRate}%)</span>
                    <span className="font-mono font-medium">{formatCurrency(taxAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between w-full max-w-xs text-base font-bold border-t pt-2">
                  <span>Total</span>
                  <span className="font-mono">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <FormActions
            onCancel={() => navigate(isEditMode && invoiceId ? `/invoices/${invoiceId}` : '/invoices')}
            isSubmitting={activeMutation.isPending}
            submitLabel={isEditMode ? 'Update Invoice' : 'Create Invoice'}
            submittingLabel={isEditMode ? 'Updating...' : 'Creating...'}
          />
        </form>
      </Form>

      {/* Dialogs */}
      <AddInvoiceLineItemDialog
        isOpen={showCustomDialog}
        onClose={() => setShowCustomDialog(false)}
        onSubmit={handleCustomItemSubmit}
      />
      <InvoiceCatalogPicker
        isOpen={showCatalogPicker}
        onClose={() => setShowCatalogPicker(false)}
        onSubmit={handleCatalogItemSubmit}
      />
      <EditLineItemDialog
        isOpen={editingLineItemIndex !== null}
        onClose={() => setEditingLineItemIndex(null)}
        lineItem={editingLineItemIndex !== null ? {
          description: lineItems[editingLineItemIndex]?.description || '',
          base_cost: lineItems[editingLineItemIndex]?.base_cost || 0,
          base_unit: lineItems[editingLineItemIndex]?.base_unit || 'item',
          markup_percentage: lineItems[editingLineItemIndex]?.markup_percentage || 0,
          unit_cost: lineItems[editingLineItemIndex]?.unit_cost || 0,
          source_type: lineItems[editingLineItemIndex]?.source_type || 'custom',
          source_item_id: lineItems[editingLineItemIndex]?.source_item_id,
        } : null}
        onSave={(updates) => {
          if (editingLineItemIndex !== null) {
            const items = [...form.getValues('line_items')];
            const item = { ...items[editingLineItemIndex] };
            item.description = updates.description;
            item.base_cost = updates.base_cost;
            item.base_unit = updates.base_unit;
            item.markup_percentage = updates.markup_percentage;
            item.unit_cost = updates.base_cost * (1 + updates.markup_percentage / 100);
            items[editingLineItemIndex] = item;
            form.setValue('line_items', items, { shouldValidate: true });
          }
        }}
      />
    </div>
  );
};

export default StandaloneInvoiceForm;
